import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import { usePrevious } from "hooks";
import { SetMatchDataType } from "types";

import {
	addSpans,
	ChangedNodeObjectType,
	initMatchData,
	restoreOriginNodes,
	searchAcrossNodes,
} from "./helpers";

import styles from "./pageSearch.module.scss";

type PageSearchWrapperProps = {
	index: number;
	searchString: string;
	setMatchData?: SetMatchDataType;
	children: React.ReactElement;
};

const SEARCH_MIN_LENGTH = 3;

const PageSearchWrapper = ({
	index,
	searchString,
	setMatchData,
	children,
}: PageSearchWrapperProps) => {
	const parentRef = useRef<HTMLDivElement>(null);
	const ref = useRef<HTMLDivElement>(null);

	const [originNodes, setOriginNodes] = useState<ChangedNodeObjectType[]>();

	const prevSearchString = usePrevious(searchString);

	const setMatchDataFn = useCallback(
		count => {
			setMatchData?.(index, count, count ? parentRef.current : null);
		},
		[index, setMatchData],
	);

	const searchText = useCallback(
		(text: string) => {
			setMatchDataFn(0);

			// Restore original Node elements before each search
			restoreOriginNodes(originNodes);

			if (text?.length < SEARCH_MIN_LENGTH) {
				return;
			}

			const textNodes: ChildNode[] | undefined = [];

			// Extract text from nodes
			const extractText = (nodes: NodeListOf<ChildNode> | undefined) => {
				nodes?.forEach(node => {
					if (node.nodeType === node.ELEMENT_NODE) {
						extractText(node.childNodes);
					} else if (node.nodeType === node.TEXT_NODE) {
						textNodes.push(node);
					}
				});
			};

			extractText(ref.current?.childNodes);

			if (!textNodes.length) {
				return;
			}

			// Combine text to one string
			let textCombined = "";

			textNodes.forEach((node: ChildNode) => {
				textCombined += node.textContent;
			});

			const regexp = new RegExp(text, "ig");
			const matches = textCombined.matchAll(regexp);

			let matchCount = 0;

			const [matchData, addMatchData] = initMatchData();

			// Iterate trough matches to make node map
			for (const match of matches) {
				if (match.index === undefined) {
					return;
				}

				matchCount++;

				searchAcrossNodes(
					textNodes,
					match.index,
					match.index + match[0].length - 1,
					addMatchData,
				);
			}
			setMatchDataFn(matchCount);

			// Add spans to selected nodes
			addSpans(matchData, setOriginNodes, styles.textHighlight);
		},
		[originNodes, setMatchDataFn],
	);

	useEffect(() => {
		if (searchString !== undefined && searchString !== prevSearchString) {
			searchText(searchString);
		}
	}, [prevSearchString, searchString, searchText]);

	return (
		<div ref={parentRef}>
			<div ref={ref}>{children}</div>
		</div>
	);
};

export default memo(PageSearchWrapper);
